import { cn } from '../../lib/cn';

type Props = {
  title: string;
  subtitle?: string;
  currentValue: number;
  currentLabel: string;
  scrubValue?: number | null;
  scrubLabel?: string | null;
  format: (n: number) => string;
  color?: string;
  delta?: { value: number; period: string };
  /** Render the delta on its own line below the label. Default false. */
  deltaBlock?: boolean;
  /** When delta.value is 0, swap to this string instead of "+$0.00 …". */
  zeroDeltaLabel?: string;
};

/**
 * Hero header that sits above a ScrubChart. Updates live as the user
 * drags. When `scrubValue` is null, it shows the current value plus a
 * delta chip; during scrub the delta hides and the label switches to
 * the scrubbed date.
 */
export function ScrubHeader({
  title,
  subtitle,
  currentValue,
  currentLabel,
  scrubValue,
  scrubLabel,
  format,
  color,
  delta,
  deltaBlock = false,
  zeroDeltaLabel,
}: Props) {
  const isScrubbing = scrubValue != null && scrubLabel != null;
  const value = isScrubbing ? scrubValue : currentValue;
  const label = isScrubbing ? scrubLabel : currentLabel;

  const deltaNode =
    !isScrubbing && delta ? (
      <span
        className={cn(
          'text-[13px] font-medium tabular-nums',
          delta.value > 0
            ? 'text-positive'
            : delta.value < 0
              ? 'text-negative'
              : 'text-text-tertiary',
        )}
      >
        {delta.value === 0 && zeroDeltaLabel ? (
          zeroDeltaLabel
        ) : (
          <>
            {delta.value >= 0 ? '+' : ''}
            {format(delta.value)} {delta.period}
          </>
        )}
      </span>
    ) : null;

  return (
    <div className="flex flex-col">
      <div className="text-[17px] font-semibold text-zinc-900 dark:text-zinc-100">
        {title}
      </div>
      {subtitle ? (
        <div className="text-[13px] font-medium text-zinc-500">{subtitle}</div>
      ) : null}
      <div
        className="mt-1 text-[32px] font-extralight font-mono tabular-nums tracking-tight leading-none"
        style={color ? { color } : undefined}
      >
        {format(value)}
      </div>
      {deltaBlock ? (
        <>
          <div className="mt-1 text-[13px] text-text-tertiary min-h-[18px]">
            {label}
          </div>
          {deltaNode ? <div className="mt-0.5">{deltaNode}</div> : null}
        </>
      ) : (
        <div className="mt-1 flex items-baseline gap-2 min-h-[18px]">
          <span className="text-[13px] text-text-tertiary">{label}</span>
          {deltaNode}
        </div>
      )}
    </div>
  );
}

